import { twMerge } from "tailwind-merge";

import { GenericExternalLink } from "@components/generic-external-link";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  PartnerPermissionEnum,
  type Partner,
} from "@north-beam/nb-client-side";
import Spinner from "@shared/spinner";
import { Switch } from "@shared/switch";

import { usePartnerConsent } from "../hooks/use-partner-consent";

interface PartnerConsentSwitchProps {
  partner: Partner;
  permissions: PartnerPermissionEnum[];
  label: string;
  hideWhenMissingConsent?: boolean;
  learnMoreUrl?: string;
}

const PartnerConsentSwitch = ({
  partner,
  permissions = [PartnerPermissionEnum.data_exports],
  label,
  hideWhenMissingConsent,
  learnMoreUrl,
}: PartnerConsentSwitchProps) => {
  const { user } = useUser();

  const {
    loading: isLoadingConsent,
    hasProvidedConsent,
    canToggleConsent,
    canViewConsent: canDisplayPartnerConsent,
    togglePartnerConsent,
    isUpdatingConsent,
  } = usePartnerConsent({
    user,
    partner,
    permissions,
    hideWhenMissingConsent: hideWhenMissingConsent,
  });

  const displayLoadingSpinner = isLoadingConsent || isUpdatingConsent;
  const isSwitchDisabled = !canToggleConsent || displayLoadingSpinner;

  const onClick = () => {
    if (isSwitchDisabled) {
      return;
    }

    togglePartnerConsent();
  };

  if (!canDisplayPartnerConsent) {
    return null;
  }

  return (
    <GenericTooltip
      content="Only the account admin can update the partner consent"
      showTooltip={!canToggleConsent}
      placement="top-start"
      noInfoCircle
      hideArrow
    >
      <div
        className={twMerge(
          "flex items-center gap-2 pb-1 cursor-pointer font-semibold text-sm",
          isSwitchDisabled && "cursor-not-allowed text-subtitle font-medium",
        )}
        onClick={onClick}
      >
        {displayLoadingSpinner && <Spinner />}
        {!isLoadingConsent && (
          <Switch
            id={`partner-consent-switch-${partner}`}
            size="small"
            value={hasProvidedConsent}
            onChange={onClick}
            disabled={isSwitchDisabled}
          />
        )}
        {label}
        {learnMoreUrl && (
          <GenericExternalLink
            href={learnMoreUrl}
            onClick={(event) => event.stopPropagation()}
          >
            {" "}
            (learn more)
          </GenericExternalLink>
        )}
      </div>
    </GenericTooltip>
  );
};

export default PartnerConsentSwitch;
